import { motion } from "framer-motion";
import Header from "@/components/Header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ArrowUpRight, ArrowDownRight, Activity, DollarSign, Percent } from "lucide-react";
import { useCoins, fallbackCoins } from "@/hooks/useCoins";

const priceData = [
  { time: '00:00', price: 1.2, volume: 5000 },
  { time: '04:00', price: 1.4, volume: 6200 },
  { time: '08:00', price: 1.3, volume: 4800 },
  { time: '12:00', price: 1.5, volume: 7300 },
  { time: '16:00', price: 1.6, volume: 8100 },
  { time: '20:00', price: 1.8, volume: 9400 },
  { time: '24:00', price: 1.7, volume: 8900 },
];

const Analytics = () => {
  const { data: coinsData } = useCoins(50);
  const coins = coinsData?.coins || fallbackCoins;
  const parseMoney = (value: string) => Number(value.replace(/[$,]/g, "").replace("M", "e6").replace("K", "e3")) || 0;
  const liveVolume = coins.reduce((total, coin) => total + parseMoney(coin.volume), 0);
  const liveMarketCap = coins.reduce((total, coin) => total + parseMoney(coin.marketCap), 0);
  const liveChart = coins.slice(0, 12).map((coin) => ({ time: coin.name, price: parseMoney(coin.marketCap), volume: parseMoney(coin.volume) }));
  const chartData = coinsData?.isFallback ? priceData : liveChart;
  const metrics = [
    {
      title: "Current Price",
      value: coinsData?.isFallback ? "$1.70" : `$${liveMarketCap.toLocaleString()}`,
      change: coinsData?.isFallback ? "+12.5%" : "Live market-cap aggregate",
      isPositive: true,
      icon: DollarSign,
    },
    {
      title: "24h Volume",
      value: coinsData?.isFallback ? "$89,400" : `$${liveVolume.toLocaleString()}`,
      change: coinsData?.isFallback ? "+24.3%" : "Live indexed volume",
      isPositive: true,
      icon: Activity,
    },
    {
      title: "Reserve Ratio",
      value: coinsData?.isFallback ? "85%" : `${coins.length} tokens`,
      change: coinsData?.isFallback ? "-2.1%" : "Production records",
      isPositive: false,
      icon: Percent,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 pt-24 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-6"
        >
          <h1 className="text-3xl font-bold">Analytics Dashboard</h1>
          
          {/* Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {metrics.map((metric, index) => (
              <Card key={index} className="glass-card">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {metric.title}
                  </CardTitle>
                  <metric.icon className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{metric.value}</div>
                  <div className={`flex items-center text-sm ${
                    metric.isPositive ? 'text-green-500' : 'text-red-500'
                  }`}>
                    {metric.isPositive ? (
                      <ArrowUpRight className="h-4 w-4 mr-1" />
                    ) : (
                      <ArrowDownRight className="h-4 w-4 mr-1" />
                    )}
                    {metric.change}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 gap-6">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>{coinsData?.isFallback ? "Price & Volume Trends" : "Market Cap & Volume by Token"}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="time" 
                        className="text-muted-foreground"
                      />
                      <YAxis 
                        yAxisId="left"
                        className="text-muted-foreground"
                      />
                      <YAxis 
                        yAxisId="right" 
                        orientation="right"
                        className="text-muted-foreground"
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'rgba(0,0,0,0.8)',
                          border: '1px solid rgba(255,255,255,0.1)'
                        }}
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="price"
                        stroke="#8247E5"
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="volume"
                        stroke="#A78BFA"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default Analytics;
